import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FormRenderer from "@/components/price/FormRenderer";

const SKIP_KEY = "price_form_skipped";

/** Fenêtre questionnaire à l'ouverture des dashboards Price. */
export default function PendingFormPopup() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const onDashboard = /^\/dashboard\/(client|driver)\/?$/.test(pathname);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/price/forms/pending");
      const skipped = JSON.parse(sessionStorage.getItem(SKIP_KEY) || "[]");
      setData(r.data?.form && !skipped.includes(r.data.token) ? r.data : null);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!user || !onDashboard) return undefined;
    const t0 = setTimeout(load, 2500);
    const t = setInterval(() => { if (!document.hidden) load(); }, 300000);
    return () => { clearTimeout(t0); clearInterval(t); };
  }, [user, onDashboard, load]);

  if (!data || !onDashboard) return null;

  const later = () => {
    const skipped = JSON.parse(sessionStorage.getItem(SKIP_KEY) || "[]");
    sessionStorage.setItem(SKIP_KEY, JSON.stringify([...skipped, data.token]));
    setData(null);
  };

  const submit = async (answers) => {
    setSending(true);
    try {
      await api.post(`/price/forms/public/${data.token}`, { answers });
      toast.success("Merci pour tes réponses 🙏");
      setData(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
      if (e?.response?.status === 409) setData(null);
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 sm:items-center" data-testid="pending-form-popup">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-[#FFF8F1] p-5 street-border sm:rounded-3xl"
        style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-black uppercase tracking-widest text-primary">Questionnaire</div>
            <h2 className="mt-1 font-[Outfit] text-[22px] font-black leading-tight">{data.form.title}</h2>
          </div>
          <button onClick={later} className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-white street-border-thin" data-testid="pending-form-later" aria-label="Plus tard">
            <X className="h-4 w-4" />
          </button>
        </div>
        {data.form.description && <p className="mt-2 text-[14px] text-foreground/70">{data.form.description}</p>}
        <div className="mt-4"><FormRenderer form={data.form} onSubmit={submit} submitting={sending} /></div>
        <button onClick={later} className="mt-3 w-full py-2 text-[13px] font-bold text-foreground/50" data-testid="pending-form-later-text">Plus tard</button>
      </div>
    </div>
  );
}
